/**
 * The wire contract with `dominopeak-marketing-api`.
 *
 * Every endpoint answers with the same envelope, produced by
 * `App\Support\Api\ApiResponse`. Errors come from `App\Exceptions\ApiExceptionRenderer`.
 * These types are the TypeScript half of that contract; if the PHP side changes
 * shape, this file changes with it.
 */

export interface ApiEnvelope<TData> {
  data: TData;
  meta: Record<string, unknown>;
  links: Record<string, string | null>;
  message: string | null;
}

export interface PaginationMeta extends Record<string, unknown> {
  current_page: number;
  per_page: number;
  total: number;
  last_page: number;
  from: number | null;
  to: number | null;
}

export interface PaginationLinks extends Record<string, string | null> {
  first: string | null;
  last: string | null;
  prev: string | null;
  next: string | null;
  self: string | null;
}

export interface PaginatedEnvelope<TItem> extends ApiEnvelope<TItem[]> {
  meta: PaginationMeta;
  links: PaginationLinks;
}

/** A page of results, already unwrapped from the envelope. */
export interface Page<TItem> {
  items: TItem[];
  meta: PaginationMeta;
}

/**
 * The error body. `errors` is only present on 422; every other failure carries a
 * `code` the UI can branch on without string-matching the message.
 */
export interface ApiErrorBody {
  message: string;
  code: string;
  errors?: Record<string, string[]>;
  context?: Record<string, unknown>;
}

/** Codes the UI reacts to structurally. Any other string is shown as-is. */
export const API_ERROR_CODES = {
  validationFailed: 'validation_failed',
  unauthenticated: 'unauthenticated',
  forbidden: 'forbidden',
  notFound: 'not_found',
  conflict: 'conflict',
  rateLimited: 'rate_limited',
  serverError: 'server_error',
  network: 'network_error',
} as const;

export type ApiErrorCode = (typeof API_ERROR_CODES)[keyof typeof API_ERROR_CODES] | (string & {});
